/**
 * The 404.
 *
 * Written in the product's own vocabulary rather than in stock apology: the
 * missing page is presented as a training record that did not attend. It is a
 * joke for people who know the domain, and it is also the fastest way to say
 * "this is our application, and it is working" instead of the browser's own
 * error page, which says nothing and looks like a fault in the server.
 *
 * No product name anywhere: branding is instance configuration by design, and
 * a hard-coded name here would be the one place it leaked.
 */

import { Link } from "@tanstack/react-router"
import { Home, SearchX } from "lucide-react"

import { Badge, Button, buttonClasses, Card, CardContent } from "@/components/ui"

export function NotFound() {
  return (
    <div className="mx-auto flex max-w-lg flex-col items-center px-4 py-16 text-center">
      <SearchX
        className="mb-4 h-10 w-10 text-muted-foreground"
        aria-hidden
      />

      <p className="font-display text-5xl font-bold">Not found</p>
      <h1 className="mt-3 text-xl">This page did not attend</h1>
      <p className="mt-2 text-sm text-muted-foreground">
        We checked the register and there is no record of it. It may have been
        renamed, moved, or it never enrolled in the first place.
      </p>

      {/* The joke, and the house style, in one: a record with an attendance
          status, and no sign-off, because nobody could vouch for it. */}
      <Card className="mt-6 w-full text-left">
        <CardContent className="space-y-2 text-sm">
          <Row label="Intervention" value="Locating the requested page" />
          <Row label="Learner" value="The address you followed" />
          <div className="flex items-baseline justify-between gap-4">
            <span className="text-xs text-muted-foreground">Attendance</span>
            <Badge tone="danger">No-show</Badge>
          </div>
          <Row label="Signed off" value="Never" />
        </CardContent>
      </Card>

      <div className="mt-6 flex flex-wrap justify-center gap-2">
        {/* A real anchor, not a button that navigates: this is the one control
            on the page a lost user may well want to open in a new tab. */}
        <Link to="/" className={buttonClasses()}>
          <Home className="h-4 w-4" aria-hidden />
          Back to the dashboard
        </Link>
        <Button variant="secondary" onClick={() => window.history.back()}>
          Go back
        </Button>
      </div>
    </div>
  )
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span>{value}</span>
    </div>
  )
}
